import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Block, CheckCircleOutline } from "@mui/icons-material";

import { useGetCustomers } from "../../hooks/customers/useGetCustomers";
import useTableActions from "../../hooks/common/useTableActions";

import TableLayout from "../global/Table/TableLayout";
import ExportButton from "../exports/ExportButton";
import { CustomersTableColumns } from "./Table/CustomersTableColumns";
import CustomersTableRow from "./Table/CustomersTableRow";
import { usePermissions } from "../../auth/PermissionsContext";
import BulkActionBar from "../bulk/BulkActionBar";
import BulkResultDialog from "../bulk/BulkResultDialog";
import useBulkClientIterate from "../bulk/useBulkClientIterate";
import { updateCustomerStatus } from "../../api/apiCustomers";

const SORT_OPTIONS = [
    { value: "newest",        labelEn: "Newest",        labelAr: "الأحدث" },
    { value: "oldest",        labelEn: "Oldest",        labelAr: "الأقدم" },
    { value: "name",          labelEn: "Name",          labelAr: "الاسم" },
    { value: "total_spent",   labelEn: "Total Spent",   labelAr: "إجمالي الإنفاق" },
    { value: "orders_count",  labelEn: "Orders Count",  labelAr: "عدد الطلبات" },
];

const CustomersTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    const { has } = usePermissions();
    const canManage = has("customers.manage") || has("customers.update");

    const [statusFilter, setStatusFilter] = useState("");
    const [hasOrdersFilter, setHasOrdersFilter] = useState("");
    const [sortFilter, setSortFilter] = useState("newest");
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkRequested, setBulkRequested] = useState(0);

    const {
        page,
        perPage,
        search,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleView,
        handleRequestSort,
        order,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/customers",
    });

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        search: search || undefined,
        status: statusFilter || undefined,
        has_orders: hasOrdersFilter === "" ? undefined : hasOrdersFilter,
        sort: sortFilter || undefined,
    }), [page, perPage, search, statusFilter, hasOrdersFilter, sortFilter]);

    const { customers, meta, isLoading, error, refetch } = useGetCustomers(params);

    const queryClient = useQueryClient();
    const { run: runBulk, isRunning: isBulkRunning } = useBulkClientIterate();

    // Filter out rows already in the target state — Activate on already-active
    // customers is a no-op and just touches updated_at. Same for Ban.
    const selectedCustomers = customers.filter((c) => selectedIds.includes(c.id));
    const knownBannedCount = selectedCustomers.filter((c) => c.status === 'banned').length;
    const knownActiveCount = selectedCustomers.filter((c) => c.status === 'active').length;
    const allKnownBanned = selectedCustomers.length > 0 && knownBannedCount === selectedCustomers.length;
    const allKnownActive = selectedCustomers.length > 0 && knownActiveCount === selectedCustomers.length;

    const handleBulkSetStatus = async (status) => {
        if (!selectedIds.length) return;
        const idsToHit = selectedCustomers.length
            ? selectedCustomers.filter((c) => c.status !== status).map((c) => c.id)
            : selectedIds;
        if (idsToHit.length === 0) return;
        setBulkRequested(idsToHit.length);
        const result = await runBulk(idsToHit, (id) =>
            updateCustomerStatus(id, { status })
        );
        queryClient.invalidateQueries({ queryKey: ["customers"] });
        setBulkResult(result);
    };

    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "active", label: { ar: "نشط",  en: "Active" } },
                { value: "banned", label: { ar: "محظور", en: "Banned" } },
            ],
        },
        {
            name: "has_orders",
            label: { ar: "لديه طلبات", en: "Has Orders" },
            value: hasOrdersFilter,
            onChange: setHasOrdersFilter,
            options: [
                { value: "",  label: { ar: "الكل",          en: "All" } },
                { value: "1", label: { ar: "لديه طلبات",    en: "With orders" } },
                { value: "0", label: { ar: "بدون طلبات",    en: "Without orders" } },
            ],
        },
        {
            name: "sort",
            label: { ar: "الترتيب", en: "Sort" },
            value: sortFilter,
            onChange: setSortFilter,
            options: SORT_OPTIONS.map((o) => ({
                value: o.value,
                label: { ar: o.labelAr, en: o.labelEn },
            })),
        },
    ];

    const renderRow = (rowParams) => (
        <CustomersTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const bulkActions = canManage
        ? [
            {
                key: "ban",
                label: isArabic ? "حظر" : "Ban",
                icon: <Block />,
                color: "error",
                disabled: isBulkRunning || allKnownBanned,
                tooltip: allKnownBanned
                    ? (isArabic ? 'كل المحدد محظور بالفعل' : 'All selected are already banned')
                    : '',
                onClick: () => handleBulkSetStatus("banned"),
            },
            {
                key: "unban",
                label: isArabic ? "إلغاء الحظر" : "Unban",
                icon: <CheckCircleOutline />,
                color: "success",
                variant: "outlined",
                disabled: isBulkRunning || allKnownActive,
                tooltip: allKnownActive
                    ? (isArabic ? 'كل المحدد نشط بالفعل' : 'All selected are already active')
                    : '',
                onClick: () => handleBulkSetStatus("active"),
            },
        ]
        : [];

    return (
        <>
            <BulkActionBar
                selectedCount={selectedIds.length}
                actions={bulkActions}
                onClear={() => handleSelectAllClick({ target: { checked: false } }, customers)}
            />
            <BulkResultDialog
                open={Boolean(bulkResult)}
                result={bulkResult}
                totalRequested={bulkRequested}
                onClose={() => setBulkResult(null)}
            />
        <TableLayout
            title={isArabic ? "العملاء" : "Customers"}
            subtitle={isArabic ? "إدارة قاعدة العملاء" : "Manage customer base"}
            search={search}
            onSearchChange={setSearch}
            addButtonVisible={false}
            headerExtra={<ExportButton type="customers" filters={params} variant="contained" size="medium" />}
            isLoading={isLoading}
            error={error}
            items={customers}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={CustomersTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuView={handleView}
            menuDisableEdit
            menuDisableDelete
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, customers)}
            onRowCheckboxClick={handleRowCheckboxClick}
            bulkDeleteDisabled
            filters={tableFilters}
            onClearAllFilters={() => {
                setStatusFilter("");
                setHasOrdersFilter("");
                setSortFilter("newest");
            }}
            emptyLabelAr="لا يوجد عملاء بعد"
            emptyLabelEn="No customers yet"
            refetch={refetch}
        />
        </>
    );
};

export default CustomersTable;
